/**
 * @file FlipClock.tsx
 * @brief Odometer-style digit-roll timer — Silero FlipClock animation, boardroom mono font.
 *
 * @details
 * Same upward digit roll as FRONTEND CircularProgressMic FlipDigit (320ms
 * cubic-out, 9→0 rollover via trailing 0). Typography stays `font-mono`
 * tabular-nums to match the previous boardroom timer look.
 *
 * 04-10-2026: `direction="down"` for countdowns (Home's scheduled card) and
 * `inheritFont` so the digits take the surrounding text's font and weight.
 * Defaults are unchanged, so the boardroom timer is as it was.
 *
 * @author DES
 * @date 28-07-2026
 */

import { useEffect, useRef, useState } from "react";
import { cn } from "@/shared/theme/cn";

const FLIP_MS = 320;

function FlipDigit({
  digit,
  fontSize,
  direction,
  glyphClass,
}: {
  digit: number;
  fontSize: number;
  direction: "up" | "down";
  glyphClass: string;
}) {
  const digitH = Math.round(fontSize * 1.08);
  const [offset, setOffset] = useState(digit);
  const [animate, setAnimate] = useState(false);
  const prev = useRef(digit);

  useEffect(() => {
    const from = prev.current;
    if (digit === from) return;

    if (direction === "down") {
      // Counting down: roll DOWN to the new digit; on 0→9 jump (unanimated)
      // to the trailing 0 at index 10, then roll down from it to 9.
      prev.current = digit;
      if (digit > from) {
        setAnimate(false);
        setOffset(10);
        const r = window.requestAnimationFrame(() =>
          window.requestAnimationFrame(() => {
            setAnimate(true);
            setOffset(digit);
          }),
        );
        const t = window.setTimeout(() => setAnimate(false), FLIP_MS + 40);
        return () => {
          window.cancelAnimationFrame(r);
          window.clearTimeout(t);
        };
      }
      setAnimate(true);
      setOffset(digit);
      const t = window.setTimeout(() => setAnimate(false), FLIP_MS);
      return () => window.clearTimeout(t);
    }

    if (digit < from) {
      setAnimate(true);
      setOffset(10);
      const t = window.setTimeout(() => {
        setAnimate(false);
        setOffset(0);
      }, FLIP_MS);
      prev.current = digit;
      return () => window.clearTimeout(t);
    }

    setAnimate(true);
    setOffset(digit);
    prev.current = digit;
    const t = window.setTimeout(() => setAnimate(false), FLIP_MS);
    return () => window.clearTimeout(t);
  }, [digit, direction]);

  return (
    <span
      className="relative inline-block overflow-hidden"
      style={{ height: digitH, width: Math.ceil(fontSize * 0.62) }}
      aria-hidden
    >
      <span
        className="absolute left-0 right-0 top-0 flex flex-col items-center"
        style={{
          transform: `translate3d(0, ${-offset * digitH}px, 0)`,
          transition: animate
            ? `transform ${FLIP_MS}ms cubic-bezier(0.33, 1, 0.68, 1)`
            : "none",
        }}
      >
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((n, i) => (
          <span
            key={i}
            className={cn("flex items-center justify-center tabular-nums tracking-tight", glyphClass)}
            style={{
              height: digitH,
              lineHeight: `${digitH}px`,
              fontSize,
            }}
          >
            {n}
          </span>
        ))}
      </span>
    </span>
  );
}

type FlipClockProps = {
  value: string;
  fontSize?: number;
  className?: string;
  /** `down` for countdowns; default `up`. */
  direction?: "up" | "down";
  /** Digits in the surrounding font/weight instead of mono (card use). */
  inheritFont?: boolean;
};

export function FlipClock({
  value,
  fontSize = 36,
  className,
  direction = "up",
  inheritFont = false,
}: FlipClockProps) {
  const digitH = Math.round(fontSize * 1.08);
  const glyphClass = inheritFont ? "" : "font-mono font-normal";

  return (
    <span
      className={cn("inline-flex items-center", !inheritFont && "font-mono", className)}
      aria-label={value}
      role="timer"
    >
      {value.split("").map((ch, i) =>
        /\d/.test(ch) ? (
          <FlipDigit
            key={`d-${i}`}
            digit={Number(ch)}
            fontSize={fontSize}
            direction={direction}
            glyphClass={glyphClass}
          />
        ) : (
          <span
            key={`s-${i}`}
            className={cn("tabular-nums tracking-tight", glyphClass)}
            style={{
              height: digitH,
              lineHeight: `${digitH}px`,
              fontSize,
              marginLeft: 1,
              marginRight: 1,
            }}
            aria-hidden
          >
            {ch}
          </span>
        ),
      )}
    </span>
  );
}
